import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Pause, Volume2, VolumeX } from "lucide-react";

const REEL_VIDEO_URL = "https://customer-assets.emergentagent.com/job_disruptiva-redesign/artifacts/s71pz6jp_Corte%20finalFV.MOV";

export const VideoReelModal = ({ isOpen, onClose }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPlaying(false);
    }
  }, [isOpen]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9998] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
          onClick={onClose}
          data-testid="video-reel-modal"
        >
          {/* Close */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
            onClick={onClose}
            className="absolute top-5 right-5 md:top-8 md:right-8 p-3 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/50 hover:text-white hover:bg-white/[0.08] transition-all z-10"
            data-testid="close-reel-btn"
          >
            <X className="w-5 h-5" />
          </motion.button>

          {/* Widescreen container */}
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-[1300px] aspect-video rounded-2xl overflow-hidden border border-white/[0.06] bg-black relative"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              ref={videoRef}
              src={REEL_VIDEO_URL}
              className="w-full h-full object-cover"
              playsInline
              muted={muted}
              loop
              preload="metadata"
              onClick={togglePlay}
            />

            {/* Play overlay */}
            {!playing && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 cursor-pointer"
                onClick={togglePlay}
              >
                <div className="reel-play-btn" data-testid="reel-play-btn">
                  <Play className="w-10 h-10 text-white ml-1" />
                </div>
                <p className="text-white/25 font-space text-sm mt-6 tracking-wide">
                  Reel Disruptiva — Corte Final
                </p>
              </div>
            )}

            {/* Controls bar */}
            <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent">
              <button
                onClick={togglePlay}
                className="p-2 text-white/60 hover:text-white transition-colors"
              >
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMuted(!muted)}
                className="p-2 text-white/60 hover:text-white transition-colors"
                data-testid="reel-modal-mute"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
